"use client";

import Link from "next/link";
import { ArrowUpRight, Plus } from "lucide-react";
import TextGenerateEffect from "@/components/TextGenerateEffect";
import TopGradient from "@/components/TopGradient";
import { RevealGroup, RevealItem } from "@/components/RevealStack";
import BOOKS from "./data";

/**
 * Books index — fabrica-style editorial layout.
 *   - Hero: giant "Library." headline + kicker/sub description.
 *   - Grid of three books: cover left, title + subtitle + description on
 *     the right, with "Read more" link through to the book detail page
 *     and a "Buy on Amazon" pill.
 */
export default function BooksPage() {
  return (
    <main className="bk-page">
      <TopGradient />
      {/* Hero */}
      <section className="bk-hero" data-bg-tint="warm">
        <TextGenerateEffect
          as="h1"
          words="Library."
          className="bk-hero-title"
          duration={0.9}
          staggerDelay={0.07}
          filter={false}
        />

        <div className="bk-hero-sub">
          <div className="bk-hero-tag">
            <Plus size={16} strokeWidth={2.5} aria-hidden />
            <span>Published works</span>
          </div>
          <p className="bk-hero-lead">
            <strong>Three books across medicine, memoir, and family fiction</strong>
            <span> — from a neurosurgeon's practical textbook to patient stories and a pandemic-era children's tale.</span>
          </p>
          <p className="bk-hero-aside">
            Paperback and Kindle editions available on Amazon — with sample
            reads for each title.
          </p>
        </div>
      </section>

      {/* Book rows */}
      <RevealGroup as="section" className="bk-list" data-bg-tint="peach">
        {BOOKS.map((book, i) => (
          <RevealItem as="article" key={book.slug} className="bk-card">
            <Link
              href={`/books/${book.slug}`}
              className="bk-card-cover"
              aria-label={`Read more about ${book.title}`}
            >
              <img src={book.image} alt={book.title} />
            </Link>

            <div className="bk-card-body">
              <div className="bk-card-meta">
                <span className="bk-card-num">
                  ({String(i + 1).padStart(2, "0")})
                </span>
                <span className="bk-card-genre">{book.genre}</span>
                <span className="bk-card-year">{book.published}</span>
              </div>

              <h2 className="bk-card-title">{book.title}</h2>
              <p className="bk-card-subtitle">{book.subtitle}</p>

              <p className="bk-card-excerpt">
                {book.description.length > 280
                  ? book.description.slice(0, 277).trim() + "…"
                  : book.description}
              </p>

              <div className="bk-card-actions">
                {book.paperback || book.kindle ? (
                  <a
                    href={book.paperback || book.kindle}
                    target="_blank"
                    rel="noreferrer"
                    className="bk-buy"
                  >
                    <span>Buy on Amazon</span>
                    <span className="bk-buy-dot" aria-hidden />
                  </a>
                ) : null}
                <Link href={`/books/${book.slug}`} className="bk-readmore">
                  <span>Read more</span>
                  <ArrowUpRight size={16} aria-hidden />
                </Link>
              </div>
            </div>
          </RevealItem>
        ))}
      </RevealGroup>
    </main>
  );
}
